import { useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	CircularProgress,
	FormControl,
	MenuItem,
	Select,
	Typography,
} from '@mui/material';

const SingleSelect = ({
	value = '',
	options = [],
	disabled = false,
	loading = false,
	size = 'small',
	onChange,
	errorMessage,
	placeholder = '',
	dataTestId = 'single-select',
	menuProps,
}) => {
	const [localError, setLocalError] = useState(null);

	const handleChange = async (event) => {
		const nextValue = event.target.value;

		if (nextValue === value) return;

		setLocalError(null);

		try {
			await onChange?.(nextValue);
		} catch (error) {
			setLocalError(error?.message || 'Unable to update. Please try again.');
		}
	};

	const renderSelectedValue = (selected) => {
		if (!selected) {
			return (
				<Typography
					component='span'
					sx={{
						color: 'text.disabled',
						fontSize: 'inherit',
					}}
				>
					{placeholder}
				</Typography>
			);
		}

		// Display the label for object options while preserving the stored value
		const selectedOption = options.find((option) =>
			typeof option === 'string'
				? option === selected
				: option.value === selected
		);

		return typeof selectedOption === 'string'
			? selectedOption
			: (selectedOption?.label ?? selected);
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
			<FormControl fullWidth size={size} disabled={disabled || loading}>
				<Select
					data-testid={dataTestId}
					value={value}
					onChange={handleChange}
					displayEmpty
					MenuProps={{
						...menuProps,
						PaperProps: {
							...menuProps?.PaperProps,
							sx: {
								borderRadius: '8px',
								overflow: 'hidden',
								...menuProps?.PaperProps?.sx,
							},
						},
						MenuListProps: {
							...menuProps?.MenuListProps,
							sx: {
								maxHeight: 240,
								maxWidth: '100%',
								overflowY: 'auto',
								...menuProps?.MenuListProps?.sx,
							},
						},
					}}
					renderValue={renderSelectedValue}
					sx={{
						fontSize: '0.875rem',
						minHeight: 32,
						backgroundColor: 'background.paper',
					}}
					endAdornment={
						loading ? (
							<CircularProgress size={16} color='inherit' sx={{ mr: 3 }} />
						) : null
					}
				>
					{options.map((option) => {
						const item =
							typeof option === 'string'
								? { label: option, value: option }
								: option;

						return (
							<MenuItem
								key={item.value}
								value={item.value}
								sx={{ fontSize: '0.875rem' }}
							>
								{item.label}
							</MenuItem>
						);
					})}
				</Select>
			</FormControl>

			{(errorMessage || localError) && (
				<Typography fontSize={11} color='error.main'>
					{errorMessage || localError}
				</Typography>
			)}
		</Box>
	);
};

SingleSelect.propTypes = {
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	options: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({
				label: PropTypes.node.isRequired,
				value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
					.isRequired,
			}),
		])
	),
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'medium']),
	onChange: PropTypes.func,
	errorMessage: PropTypes.string,
	placeholder: PropTypes.string,
	dataTestId: PropTypes.string,
	menuProps: PropTypes.object,
};

export default SingleSelect;
